import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router';
import {
  MagnifyingGlassIcon,
  KeyIcon,
  FolderIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useDebounce } from '@/hooks/useDebounce';
import {
  searchApi,
  GlobalSearchResult,
  CredentialSearchResult,
  KBArticleSearchResult,
  FolderSearchResult,
  KBFolderSearchResult,
} from '@/lib/api';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SearchResultItem =
  | { type: 'credential'; data: CredentialSearchResult }
  | { type: 'kbArticle'; data: KBArticleSearchResult }
  | { type: 'folder'; data: FolderSearchResult }
  | { type: 'kbFolder'; data: KBFolderSearchResult };

export default function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debouncedQuery = useDebounce(query, 300);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(null);
      setError(null);
      setSelectedIndex(0);
      // Focus input after a short delay to allow transition
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults(null);
      setError(null);
      return;
    }

    const search = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await searchApi.globalSearch(debouncedQuery);
        if (response.data.success && response.data.data) {
          setResults(response.data.data);
          setSelectedIndex(0);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setLoading(false);
      }
    };

    search();
  }, [debouncedQuery]);

  // Flatten results for keyboard navigation
  const flattenedResults = useCallback((): SearchResultItem[] => {
    if (!results) return [];
    const items: SearchResultItem[] = [];

    results.credentials.forEach((c) => items.push({ type: 'credential', data: c }));
    results.kbArticles.forEach((a) => items.push({ type: 'kbArticle', data: a }));
    results.folders.forEach((f) => items.push({ type: 'folder', data: f }));
    results.kbFolders.forEach((f) => items.push({ type: 'kbFolder', data: f }));

    return items;
  }, [results]);

  // Navigate to selected item
  const navigateToItem = useCallback((item: SearchResultItem) => {
    switch (item.type) {
      case 'credential':
        // Navigate to credential detail page directly
        navigate(`/credentials/${item.data.id}`);
        break;
      case 'kbArticle':
        navigate(`/kb/articles/${item.data.id}`);
        break;
      case 'folder':
        navigate(`/credentials?folder=${item.data.id}`);
        break;
      case 'kbFolder':
        navigate(`/kb/folders/${item.data.id}`);
        break;
    }
    onClose();
  }, [navigate, onClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items = flattenedResults();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (items[selectedIndex]) {
          navigateToItem(items[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [flattenedResults, selectedIndex, navigateToItem, onClose]);

  // Calculate the actual index for rendering
  const getGlobalIndex = (type: string, localIndex: number): number => {
    if (!results) return -1;
    let offset = 0;

    if (type === 'credential') return localIndex;
    offset += results.credentials.length;

    if (type === 'kbArticle') return offset + localIndex;
    offset += results.kbArticles.length;

    if (type === 'folder') return offset + localIndex;
    offset += results.folders.length;

    if (type === 'kbFolder') return offset + localIndex;

    return -1;
  };

  const renderResultItem = (
    item: SearchResultItem,
    index: number,
    isSelected: boolean
  ) => {
    const baseClasses = `flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
      isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
    }`;

    switch (item.type) {
      case 'credential':
        return (
          <div
            key={`cred-${item.data.id}`}
            className={baseClasses}
            onClick={() => navigateToItem(item)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              {item.data.icon ? (
                <img src={item.data.icon} alt="" className="w-5 h-5 rounded" />
              ) : (
                <KeyIcon className="w-4 h-4 text-blue-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.data.name}</p>
              <p className="text-xs text-gray-500 truncate">
                {item.data.username}
                {item.data.folder && ` in ${item.data.folder.name}`}
              </p>
            </div>
            <span className="text-xs text-gray-400">Credential</span>
          </div>
        );

      case 'kbArticle':
        return (
          <div
            key={`kb-${item.data.id}`}
            className={baseClasses}
            onClick={() => navigateToItem(item)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <DocumentTextIcon className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900 truncate">{item.data.title}</p>
                {item.data.status === 'draft' && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                    Draft
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 truncate">{item.data.snippet}</p>
            </div>
            <span className="text-xs text-gray-400">Article</span>
          </div>
        );

      case 'folder':
        return (
          <div
            key={`folder-${item.data.id}`}
            className={baseClasses}
            onClick={() => navigateToItem(item)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: item.data.color ? `${item.data.color}20` : '#f3f4f6' }}
            >
              {item.data.icon ? (
                <span className="text-base">{item.data.icon}</span>
              ) : (
                <FolderIcon
                  className="w-4 h-4"
                  style={{ color: item.data.color || '#6b7280' }}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.data.name}</p>
              <p className="text-xs text-gray-500">
                {item.data.credentialCount} credential{item.data.credentialCount !== 1 ? 's' : ''}
              </p>
            </div>
            <span className="text-xs text-gray-400">Folder</span>
          </div>
        );

      case 'kbFolder':
        return (
          <div
            key={`kbfolder-${item.data.id}`}
            className={baseClasses}
            onClick={() => navigateToItem(item)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${item.data.color}20` }}
            >
              <FolderOpenIcon className="w-4 h-4" style={{ color: item.data.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.data.name}</p>
              <p className="text-xs text-gray-500 truncate">
                {item.data.description || `${item.data.articleCount} article${item.data.articleCount !== 1 ? 's' : ''}`}
              </p>
            </div>
            <span className="text-xs text-gray-400">KB Folder</span>
          </div>
        );
    }
  };

  return (
    <Transition.Root show={isOpen}>
      <Dialog<"div"> as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
         
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
           
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all">
              {/* Search Input */}
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  className="h-12 w-full border-0 bg-transparent pl-11 pr-10 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search credentials, articles, folders..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {query && (
                  <button
                    type="button"
                    className="absolute right-3 top-3 p-1 rounded-md hover:bg-gray-100"
                    onClick={() => setQuery('')}
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>

              {/* Results */}
              {query.length > 0 && query.length < 2 && (
                <div className="border-t border-gray-100 px-4 py-8 text-center">
                  <p className="text-sm text-gray-500">Type at least 2 characters to search</p>
                </div>
              )}

              {loading && (
                <div className="border-t border-gray-100 px-4 py-8 text-center">
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                  <p className="mt-2 text-sm text-gray-500">Searching...</p>
                </div>
              )}

              {error && (
                <div className="border-t border-gray-100 px-4 py-8 text-center">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {!loading && results && results.totalCount === 0 && (
                <div className="border-t border-gray-100 px-4 py-8 text-center">
                  <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No results found for "{query}"</p>
                </div>
              )}

              {!loading && results && results.totalCount > 0 && (
                <div className="max-h-[60vh] overflow-y-auto border-t border-gray-100">
                  {/* Credentials Section */}
                  {results.credentials.length > 0 && (
                    <div>
                      <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Credentials ({results.credentials.length})
                      </div>
                      {results.credentials.map((cred, i) =>
                        renderResultItem(
                          { type: 'credential', data: cred },
                          getGlobalIndex('credential', i),
                          selectedIndex === getGlobalIndex('credential', i)
                        )
                      )}
                    </div>
                  )}

                  {/* KB Articles Section */}
                  {results.kbArticles.length > 0 && (
                    <div>
                      <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Articles ({results.kbArticles.length})
                      </div>
                      {results.kbArticles.map((article, i) =>
                        renderResultItem(
                          { type: 'kbArticle', data: article },
                          getGlobalIndex('kbArticle', i),
                          selectedIndex === getGlobalIndex('kbArticle', i)
                        )
                      )}
                    </div>
                  )}

                  {/* Credential Folders Section */}
                  {results.folders.length > 0 && (
                    <div>
                      <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Folders ({results.folders.length})
                      </div>
                      {results.folders.map((folder, i) =>
                        renderResultItem(
                          { type: 'folder', data: folder },
                          getGlobalIndex('folder', i),
                          selectedIndex === getGlobalIndex('folder', i)
                        )
                      )}
                    </div>
                  )}

                  {/* KB Folders Section */}
                  {results.kbFolders.length > 0 && (
                    <div>
                      <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        KB Folders ({results.kbFolders.length})
                      </div>
                      {results.kbFolders.map((folder, i) =>
                        renderResultItem(
                          { type: 'kbFolder', data: folder },
                          getGlobalIndex('kbFolder', i),
                          selectedIndex === getGlobalIndex('kbFolder', i)
                        )
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Footer with keyboard hints */}
              <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-2">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-600 font-mono">
                      ↑↓
                    </kbd>
                    <span>to navigate</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-600 font-mono">
                      ↵
                    </kbd>
                    <span>to select</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-600 font-mono">
                      esc
                    </kbd>
                    <span>to close</span>
                  </span>
                </div>
                {results && results.totalCount > 0 && (
                  <span className="text-xs text-gray-500">
                    {results.totalCount} result{results.totalCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
